  <div>
    <!-- Loading State -->
    <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center text-secondary py-5 min-h-400">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted mb-0">Loading compliance checklist...</p>
    </div>

    <!-- Form Container -->
    <div v-else-if="form" class="border-0">
      <div v-for="main_section in form" :key="main_section.id" class="main-section-wrapper">
        <div v-for="sub_section in main_section.sub_sections" :key="sub_section.section_id" class="border-0 overflow-hidden mb-4">
          <!-- Header -->
          <div class="card-header border-0 px-4 py-3 custom-header cursor-pointer"
               @click="toggle_section(sub_section.section_id)">
            <div class="d-flex justify-content-between align-items-center">
              <p class="mb-0 fw-semibold text-white text-capitalize">
                {{ sub_section.section_name }}
              </p>
              <i class="bi text-white"
                 :class="is_section_open(sub_section.section_id) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
          </div>

          <!-- Body -->
          <div :id="`section-${sub_section.section_id}`"
               class="collapse"
               :class="{ show: is_section_open(sub_section.section_id) }">
            <div>
              <div v-for="question in sub_section.questions"
                   :key="question.section_question_id"
                   class="bg-light border rounded-3 p-3 p-md-4 mb-3 shadow-sm">

                <div class="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-start gap-3 mb-3">
                  <div class="flex-grow-1">
                    <p class="mb-0 fw-bold text-dark text-xs text-capitalize">
                      {{ question.question_text }}
                    </p>
                  </div>
                  <div class="d-flex gap-2">
                    <button
                      type="button"
                      class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow"
                      data-bs-toggle="modal"
                      data-bs-target="#assign_questions_modal"
                      @click="set_selected_question(question, sub_section.section_name)"
                    >
                      Assign Target
                    </button>
                  </div>
                </div>

                <!-- Requirement Types Section -->
                <div v-if="question.target_questions && question.target_questions.length > 0" class="mb-3">
                  <label class="form-label fw-semibold text-xs mb-2">Select Requirement Types:</label>
                  <div class="d-flex flex-wrap gap-2">
                    <div v-for="req_type in requirement_types" :key="req_type.id" class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :id="`req-${question.section_question_id || question.id}-${req_type.id}`"
                        :checked="is_requirement_type_selected(question, req_type.name)"
                        @change="toggle_requirement_type(question, req_type.name)"
                      >
                      <label class="form-check-label text-xxs" :for="`req-${question.section_question_id || question.id}-${req_type.id}`">
                        {{ req_type.name }}
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Target Questions -->
                <div v-if="question.target_questions && question.target_questions.length > 0" class="border-top pt-3 mt-3">
                  <div class="d-flex align-items-center mb-3">
                    <p class="mb-0 text-dark text-xs">
                      Target Questions
                    </p>
                  </div>

                <div v-for="(target_q, index) in question.target_questions"
                    :key="target_q.id || index"
                    class="d-flex flex-column flex-md-row gap-3 mb-2 bg-white border rounded-3 p-3 align-items-start">

                  <div class="rounded-circle btn-primary-dark text-white d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0 badge-circle-28">
                    {{ index + 1 }}
                  </div>

                  <div class="flex-grow-1">
                    <p class="mb-1 small text-body">
                      {{ target_q.question_text }}
                    </p>
                  </div>

                  <button
                    @click="remove_target_question(question, target_q, index)"
                    class="btn btn-link p-0 text-danger fs-5 lh-1 align-self-end align-self-md-auto"
                    title="Remove target question"
                  >
                    <i class="bi bi-trash-fill text-x"></i>
                  </button>
                </div>

                </div>

                <!-- Empty State -->
                <div v-else class="text-center py-3 mt-3 bg-white border border-2 border-dashed rounded-3">
                  <i class="bi bi-inbox text-muted fs-3 d-block mb-2"></i>
                  <p class="text-muted mb-0">No target questions assigned yet</p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Assign Questions Modal -->
    <teleport to="body">
      <div class="modal fade" id="assign_questions_modal" tabindex="-1" aria-labelledby="assign_questions_modal_label" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
          <div class="modal-content bg-white rounded-3 shadow-lg border-0">
            
            <!-- Modal Header - ORIGINAL STYLING -->
            <div class="modal-header border-bottom-0 cute-header">
              <div>
                <p class="modal-title text-white text-xxs mb-1" id="assign_questions_modal_label">Assign Questions</p>
                <p class="mb-0 text-white opacity-75 text-xxs">
                  {{ selected_question_text }}
                </p>
              </div>
              <button type="button" class="modal-btn-close btn-close btn-sm" data-bs-dismiss="modal"></button>
              
            </div>

            <!-- Modal Body -->
            <div class="modal-body p-0">
              
              <!-- Filter Section -->
              <div class="bg-light border-bottom p-4">
                <div class="row g-3">
                  <!-- Form Select -->
                  <div class="col-md-4">
                    <label class="form-label fw-semibold small mb-2">Form</label>
                    <select v-model="selected_form_id" class="form-select shadow-sm">
                      <option disabled value="">Select a form...</option>
                      <option
                        v-for="form in available_forms"
                        :key="form.id"
                        :value="form.id"
                      >
                        {{ form.name }}
                      </option>
                    </select>
                  </div>

                  <!-- Section Select -->
                  <div class="col-md-4">
                    <label class="form-label fw-semibold small mb-2">Section</label>
                    <select
                      v-model="selected_section_id"
                      class="form-select shadow-sm"
                      :disabled="!selected_form_id"
                    >
                      <option disabled value="">Select a section...</option>
                      <option
                        v-for="section in available_sections"
                        :key="section.id"
                        :value="section.id"
                      >
                        {{ section.name }}
                      </option>
                    </select>
                  </div>

                  <!-- Search -->
                  <div class="col-md-4">
                    <label class="form-label fw-semibold small mb-2">Search Questions</label>
                    <input 
                      type="text" 
                      class="form-control shadow-sm" 
                      placeholder="Type to search..."
                      v-model="question_search"
                    >
                  </div>
                </div>
  
              </div>

              <!-- Questions Section -->
              <div class="p-4">
                
                <!-- Loading State -->
                <div v-if="is_loading_questions" class="text-center py-5">
                  <div class="spinner-border text-primary mb-3" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="text-muted">Loading questions...</p>
                </div>

                <!-- Questions Content -->
                <div v-else>
                  
                  <!-- Results Count -->
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <p class="text-muted text-xs mb-0">
                      Showing {{ filtered_questions.length }} of {{ question_list.length }} main questions
                    </p>
                  </div>

                  <!-- Table - ORIGINAL STYLING PRESERVED -->
                  <div class="table-responsive">
                    <table class="table table-bordered table-hover align-middle"
                          :class="{ 'opacity-50': !selected_section_id }">
                      
                      <!-- Table Head - ORIGINAL STYLING -->
                      <thead class="table-header text-white">
                        <tr class="text-xs">
                          <th class="text-center">Question</th>
                          <th class="text-center">Type</th>
                          <th class="text-center">Number</th>
                          <th class="text-center">Assign</th>
                        </tr>
                      </thead>

                      <!-- Table Body - ORIGINAL STYLING -->
                      <tbody>
                        <template v-for="section_question in filtered_questions" :key="section_question.section_question_id">
                          
                          <!-- Main Question Row -->
                          <tr class="text-xxs">
                            <td>
                              <div class="d-flex align-items-start gap-2">
                                <span class="badge text-xxs rounded-lg"
                                      :class="section_question.question?.is_assigned_as_dependent ? 'bg-warning text-dark' : 'bg-primary text-white'">
                                  {{ section_question.question?.is_assigned_as_dependent ? 'Dependent' : 'Main' }}
                                </span>
                                <div>
                                  <div class="fw-medium">{{ section_question.question?.question_text || 'No question text' }}</div>
                                </div>
                              </div>
                            </td>
                            <td>{{ format_question_type(section_question.question?.question_type) }}</td>
                            <td>{{ section_question.question?.question_number || '-' }}</td>
                            <td class="text-center">
                              <div class="form-check form-switch d-flex justify-content-center">
                                <input class="form-check-input" type="checkbox"
                                      :checked="section_question.is_assigned"
                                      :disabled="section_question.is_loading"
                                      @change="on_toggle_assign($event, section_question)" />
                              </div>
                            </td>
                          </tr>

                          <!-- Dependent Questions -->
                          <tr v-for="dependent_question in section_question.dependent_questions" :key="'dep-' + dependent_question.id" class="bg-light text-xxs">
                            <td class="ps-2">
                              <div class="d-flex align-items-start gap-2">
                                <span class="badge bg-warning text-dark text-xxs rounded-lg">Dependent</span>
                                <div>
                                  <div class="fw-medium">{{ dependent_question.question_text }}</div>
                                </div>
                              </div>
                            </td>
                            <td>{{ format_question_type(dependent_question.question_type) }}</td>
                            <td>{{ dependent_question.question_number || '-' }}</td>
                            <td><span class="badge bg-info text-white text-xxs">Auto-assigned</span></td>
                          </tr>
                        </template>

                        <!-- Empty State -->
                        <tr v-if="filtered_questions.length === 0">
                          <td colspan="4" class="text-center py-4">
                            <i class="bi bi-inbox text-muted fs-3 d-block mb-2"></i>
                            <p class="text-muted mb-0">
                              {{ !selected_section_id ? 'Please select a form and section to view questions' : 'No matching questions found' }}
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>